import React, { useState } from 'react';

import { Alert, AlertDescription } from '@app/components/ui/alert';
import { Button } from '@app/components/ui/button';
import { Separator } from '@app/components/ui/separator';
import { Spinner } from '@app/components/ui/spinner';
import { useAuth } from '@cloud-ui/contexts/AuthContext';
import { useCreateApiToken } from '@cloud-ui/hooks/useApiTokens';
import { API_BASE_URL } from '@cloud-ui/utils/api';
import type { CreateApiTokenResponseDTO } from '@shared/dto/apiTokens';

import CommandBox from '../CommandBox';

export interface CliLoginProps {
  /** Whether to show Step 3 (Share Eval) - defaults to true */
  showShareStep?: boolean;
}

/**
 * Core CLI login content that displays step-by-step instructions for logging into Promptfoo CLI.
 * This component contains just the content - wrap it in your own container (Paper, Dialog, etc.)
 */
export const CliLogin: React.FC<CliLoginProps> = ({ showShareStep = true }) => {
  const { user, organization } = useAuth();
  const createTokenMutation = useCreateApiToken({ showToasts: false });

  const [generatedToken, setGeneratedToken] = useState<CreateApiTokenResponseDTO | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateToken = async () => {
    if (!user) {
      setError('You must be logged in to generate a token');
      return;
    }

    setError(null);

    try {
      const result = await createTokenMutation.mutateAsync({
        name: `CLI Token - ${new Date().toLocaleDateString()}`,
        expiresIn: '1y',
      });
      setGeneratedToken(result);
    } catch {
      setError('Failed to generate token');
    }
  };

  const loginCommand = generatedToken
    ? `promptfoo auth login --host ${API_BASE_URL} --api-key ${generatedToken.token}`
    : `promptfoo auth login --host ${API_BASE_URL} --api-key YOUR_TOKEN_HERE`;

  return (
    <div className="space-y-8 text-left">
      <section className="space-y-4">
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-primary">Step 1: Generate an API Token</h2>
          <Separator />
        </div>
        <p className="text-sm text-muted-foreground sm:text-base">
          First, you'll need to create an API token for CLI access:
        </p>

        {!generatedToken && organization && (
          <Alert>
            <AlertDescription>
              This token will be created for: <strong>{organization.name}</strong>
            </AlertDescription>
          </Alert>
        )}

        {!generatedToken && (
          <div className="flex justify-center">
            <Button
              onClick={handleGenerateToken}
              disabled={createTokenMutation.isPending || !user}
              size="lg"
            >
              {createTokenMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Generating...
                </>
              ) : (
                'Generate CLI Token'
              )}
            </Button>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!user && (
          <Alert>
            <AlertDescription>Please log in to generate an API token.</AlertDescription>
          </Alert>
        )}

        {generatedToken && (
          <div className="space-y-4">
            {organization && (
              <Alert>
                <AlertDescription>
                  Token for organization: <strong>{organization.name}</strong>
                  {generatedToken.teamName && (
                    <>
                      {' '}
                      - Team: <strong>{generatedToken.teamName}</strong>
                    </>
                  )}
                  {!generatedToken.teamId && ' (all teams)'}
                </AlertDescription>
              </Alert>
            )}

            <Alert variant="warning">
              <AlertDescription>
                You will not be able to see this token again. Please copy it to a safe place and do
                not share it.
              </AlertDescription>
            </Alert>

            <CommandBox
              command={generatedToken.token}
              label="API token"
              copyLabel="Copy API token to clipboard"
            />
          </div>
        )}
      </section>

      <section className={showShareStep ? 'space-y-4' : 'space-y-4 pb-1'}>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-primary">Step 2: Log in via Command Line</h2>
          <Separator />
        </div>
        <p className="text-sm text-muted-foreground sm:text-base">
          {generatedToken
            ? 'Copy and run this command in your terminal:'
            : "After generating your token, you'll run this command in your terminal:"}
        </p>
        <CommandBox command={loginCommand} />
      </section>

      {showShareStep && (
        <section className="space-y-4">
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-primary">Step 3: Share Your Evaluation</h2>
            <Separator />
          </div>
          <p className="text-sm text-muted-foreground sm:text-base">
            Once logged in, you can share your latest evaluation results:
          </p>
          <CommandBox command="promptfoo share" />
        </section>
      )}
    </div>
  );
};

export default CliLogin;
